import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { ApplicationTemplateApi } from "../../../../../../../../services/Api/Admin/adminTypes";

interface ListState {
	resource: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "apps";
const resourceType = "apps";

const List = ({ resource, mobileActions }: ListState) => {
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const location = useLocation();
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onRemoveApp = async (item: ApplicationTemplateApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await adminApi.removeApp(item.id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	let config = {
		fields: [
			{
				id: "owner",
				name: t("modules.app.field.owner.title"),
				type: "text",
				render: (item: ApplicationTemplateApi) => <Link to={`/admin/apps/${item.id}`}>{item.owner}</Link>,
				renderExport: (item: ApplicationTemplateApi) => item.owner,
			},
			{
				id: "application_type",
				name: t("modules.app.field.application_type.title"),
				type: "list",
				listOptions: {
					GOORDER: t("enums.apps.application_type.GOORDER"),
					GOPOS: t("enums.apps.application_type.GOPOS"),
					GOCRM: t("enums.apps.application_type.GOCRM"),
					GOSTOCK: t("enums.apps.application_type.GOSTOCK"),
				},
				render: (item: ApplicationTemplateApi) =>
					`${t(`enums.apps.application_type.${item.application_type}`)}`,
			},
			{
				id: "provider",
				name: t("modules.app.field.provider.title"),
				render: (item: ApplicationTemplateApi) => `${t(`enums.apps.provider.${item.provider}`)}`,
			},
		],
		filters: [
			{
				id: "provider",
				name: t("modules.app.field.provider.title"),
				type: "search_select",
				source: {
					request: async () => {
						const res = await adminApi.getAppProviderTypes();
						return res.map((option) => {
							const translation = t(`enums.apps.provider.${option}`);
							return {
								id: option,
								label: translation.includes(".") ? option : translation,
							};
						});
					},
				},
				withoutInfiniteScroll: true,
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: ApplicationTemplateApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: ApplicationTemplateApi) => onRemoveApp(item),
				refreshListAfter: true,
			},
		],
		selectedColumns: ["application_type", "provider", "owner"],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			return adminApi.getApps(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	return <ListData config={config} mobileActions={mobileActions} />;
};

const AdminAppsIndexPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<any>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const buttons: ButtonProps[] = [
		{
			path: "/admin/apps/new",
			variant: "primary",
			title: t("common.action.add", { ns: "lib" }),
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			path: "/admin/apps/new",
			title: t("common.action.add", { ns: "lib" }),
		},
	];
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("common.word.applications")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("common.word.applications")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</div>
	);
};

export default AdminAppsIndexPage;
